<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, maxValue } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import { setI18nLocale } from 'dashboard/i18n';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import WootConfirmDeleteModal from 'dashboard/components/widgets/modal/ConfirmDeleteModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    BaseSettingsHeader,
    V4Button,
    WootConfirmDeleteModal,
    NextButton,
  },
  setup() {
    const { updateUISettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
      autoResolveDuration: null,
      autoResolveEmptyDuration: null,
      showDeletePopup: false,
      phoneDomain: '',
      conversationTimestampDisplayMode: 'both',
      blockDomainsWithLowReputation: true,
      includePreviousEmailMessage: true,
      resolveNewConversation: true,
      moveConversationsByOrders: false,
      extendedLabels: false,
      dialingMode: 'direct',
      lastAgentEnabled: false,
      pushNotificationMode: 'all_inboxes',
      allowToggleSignature: true,
      wallboardChatStatusEnabled: false,
      callRecordingAccess: 'admin_only',
      showFullAttachmentNames: false,
      passwordLoginMode: 'allowed_for_all',
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
    autoResolveDuration: {
      minValue: minValue(1),
      maxValue: maxValue(23976),
    },
    autoResolveEmptyDuration: {
      minValue: minValue(5),
      maxValue: maxValue(1440),
    },
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },

    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },

    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },

    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },

    getAccountId() {
      return this.id.toString();
    },
    confirmPlaceHolderText() {
      return `${this.$t(
        'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.PLACE_HOLDER',
        {
          accountName: this.name,
        }
      )}`;
    },
    isMarkedForDeletion() {
      const { custom_attributes = {} } = this.currentAccount;
      return !!custom_attributes.marked_for_deletion_at;
    },
    markedForDeletionDate() {
      const { custom_attributes = {} } = this.currentAccount;
      if (!custom_attributes.marked_for_deletion_at) return null;
      return new Date(custom_attributes.marked_for_deletion_at);
    },
    markedForDeletionReason() {
      const { custom_attributes = {} } = this.currentAccount;
      return custom_attributes.marked_for_deletion_reason || 'manual_deletion';
    },
    formattedDeletionDate() {
      if (!this.markedForDeletionDate) return '';
      return this.markedForDeletionDate.toLocaleString();
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          domain,
          support_email,
          features,
          auto_resolve_duration,
          auto_resolve_empty_duration,
          sip_host: phoneDomain,
          conversation_timestamp_display_mode: conversationTimestampDisplayMode,
          block_domains_with_low_reputation: blockDomainsWithLowReputation,
          include_previous_email_message: includePreviousEmailMessage,
          resolve_new_conversation: resolveNewConversation,
          move_conversations_by_orders: moveConversationsByOrders,
          extended_labels: extendedLabels,
          dialing_mode: dialingMode,
          last_agent_enabled: lastAgentEnabled,
          push_notification_mode: pushNotificationMode,
          allow_toggle_signature: allowToggleSignature,
          wallboard_chat_status_enabled: wallboardChatStatusEnabled,
          call_recording_access: callRecordingAccess,
          show_full_attachment_names: showFullAttachmentNames,
          password_login_mode: passwordLoginMode,
        } = this.getAccount(this.accountId);

        setI18nLocale(locale);
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
        this.autoResolveDuration = auto_resolve_duration;
        this.autoResolveEmptyDuration = auto_resolve_empty_duration;
        this.phoneDomain = phoneDomain;
        this.conversationTimestampDisplayMode =
          conversationTimestampDisplayMode;
        this.blockDomainsWithLowReputation = blockDomainsWithLowReputation;
        this.includePreviousEmailMessage = includePreviousEmailMessage;
        this.resolveNewConversation = resolveNewConversation;
        this.moveConversationsByOrders = moveConversationsByOrders;
        this.extendedLabels = extendedLabels;
        this.dialingMode = dialingMode;
        this.lastAgentEnabled = lastAgentEnabled;
        this.pushNotificationMode = pushNotificationMode;
        this.allowToggleSignature = allowToggleSignature;
        this.wallboardChatStatusEnabled = wallboardChatStatusEnabled;
        this.callRecordingAccess = callRecordingAccess;
        this.showFullAttachmentNames = showFullAttachmentNames;
        this.passwordLoginMode = passwordLoginMode || 'allowed_for_all';
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          auto_resolve_duration: this.autoResolveDuration,
          auto_resolve_empty_duration: this.autoResolveEmptyDuration,
          conversation_timestamp_display_mode:
            this.conversationTimestampDisplayMode,
          block_domains_with_low_reputation: this.blockDomainsWithLowReputation,
          include_previous_email_message: this.includePreviousEmailMessage,
          resolve_new_conversation: this.resolveNewConversation,
          move_conversations_by_orders: this.moveConversationsByOrders,
          extended_labels: this.extendedLabels,
          dialing_mode: this.dialingMode,
          last_agent_enabled: this.lastAgentEnabled,
          push_notification_mode: this.pushNotificationMode,
          allow_toggle_signature: this.allowToggleSignature,
          wallboard_chat_status_enabled: this.wallboardChatStatusEnabled,
          call_recording_access: this.callRecordingAccess,
          show_full_attachment_names: this.showFullAttachmentNames,
          password_login_mode: this.passwordLoginMode,
        });
        setI18nLocale(this.locale);
        this.getAccount(this.id).locale = this.locale;
        this.getAccount(this.id).auto_resolve_duration =
          this.autoResolveDuration;
        this.getAccount(this.id).auto_resolve_empty_duration =
          this.autoResolveEmptyDuration;
        this.getAccount(this.id).conversation_timestamp_display_mode =
          this.conversationTimestampDisplayMode;
        this.getAccount(this.id).block_domains_with_low_reputation =
          this.blockDomainsWithLowReputation;
        this.getAccount(this.id).include_previous_email_message =
          this.includePreviousEmailMessage;
        this.getAccount(this.id).resolve_new_conversation =
          this.resolveNewConversation;
        this.getAccount(this.id).move_conversations_by_orders =
          this.moveConversationsByOrders;
        this.getAccount(this.id).extended_labels = this.extendedLabels;
        this.getAccount(this.id).dialing_mode = this.dialingMode;
        this.getAccount(this.id).last_agent_enabled = this.lastAgentEnabled;
        this.getAccount(this.id).push_notification_mode =
          this.pushNotificationMode;
        this.getAccount(this.id).allow_toggle_signature =
          this.allowToggleSignature;
        this.getAccount(this.id).wallboard_chat_status_enabled =
          this.wallboardChatStatusEnabled;
        this.getAccount(this.id).call_recording_access =
          this.callRecordingAccess;
        this.getAccount(this.id).show_full_attachment_names =
          this.showFullAttachmentNames;
        this.getAccount(this.id).password_login_mode = this.passwordLoginMode;
        this.updateDirectionView(this.locale);
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    updateDirectionView(locale) {
      const isRTLSupported = getLanguageDirection(locale);
      this.updateUISettings({
        rtl_view: isRTLSupported,
      });
    },
    // Delete Function
    openDeletePopup() {
      this.showDeletePopup = true;
    },
    closeDeletePopup() {
      this.showDeletePopup = false;
    },
    async markAccountForDeletion() {
      this.closeDeletePopup();
      try {
        // Use the enterprise API to toggle deletion with delete action
        await this.$store.dispatch('accounts/toggleDeletion', {
          action_type: 'delete',
        });
        // Refresh account data
        await this.$store.dispatch('accounts/get');
        useAlert(this.$t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SUCCESS'));
      } catch (error) {
        // Handle error message
        this.handleDeletionError(error);
      }
    },
    handleDeletionError(error) {
      const errorKey = error.response?.data?.error_key;
      if (errorKey) {
        useAlert(
          this.$t(`GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.${errorKey}`)
        );
        return;
      }
      const message = error.response?.data?.message;
      if (message) {
        useAlert(message);
        return;
      }
      useAlert(this.$t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.FAILURE'));
    },
    async clearDeletionMark() {
      try {
        // Use the enterprise API to toggle deletion with undelete action
        await this.$store.dispatch('accounts/toggleDeletion', {
          action_type: 'undelete',
        });
        // Refresh account data
        await this.$store.dispatch('accounts/get');
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full">
    <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')">
      <template #actions>
        <V4Button blue :loading="isUpdating" @click="updateAccount">
          {{ $t('GENERAL_SETTINGS.SUBMIT') }}
        </V4Button>
      </template>
    </BaseSettingsHeader>
    <div class="flex-grow flex-shrink min-w-0 mt-3 overflow-auto">
      <form v-if="!uiFlags.isFetchingItem" @submit.prevent="updateAccount">
        <div
          class="flex flex-row border-b border-slate-25 dark:border-slate-800"
        >
          <div
            class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
          >
            <h4 class="text-lg font-medium text-black-900 dark:text-slate-200">
              {{ $t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE') }}
            </h4>
            <p>{{ $t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE') }}</p>
          </div>
          <div class="p-4 flex-grow-0 flex-shrink-0 flex-[70%]">
            <label :class="{ error: v$.name.$error }">
              {{ $t('GENERAL_SETTINGS.FORM.NAME.LABEL') }}
              <input
                v-model="name"
                type="text"
                :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
                @blur="v$.name.$touch"
              />
              <span v-if="v$.name.$error" class="message">
                {{ $t('GENERAL_SETTINGS.FORM.NAME.ERROR') }}
              </span>
            </label>
            <label :class="{ error: v$.locale.$error }">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL') }}
              <select v-model="locale">
                <option
                  v-for="lang in languagesSortedByCode"
                  :key="lang.iso_639_1_code"
                  :value="lang.iso_639_1_code"
                >
                  {{ lang.name }}
                </option>
              </select>
              <span v-if="v$.locale.$error" class="message">
                {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR') }}
              </span>
            </label>
            <label v-if="featureInboundEmailEnabled">
              {{ $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED') }}
            </label>
            <label v-if="featureCustomReplyDomainEnabled">
              {{
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </label>
            <label v-if="featureCustomReplyDomainEnabled">
              {{ $t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL') }}
              <input
                v-model="domain"
                type="text"
                :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
              />
            </label>
            <label v-if="featureCustomReplyEmailEnabled">
              {{ $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL') }}
              <input
                v-model="supportEmail"
                type="text"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
                "
              />
            </label>
            <label
              v-if="showAutoResolutionConfig"
              :class="{ error: v$.autoResolveDuration.$error }"
            >
              {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.LABEL') }}
              <input
                v-model="autoResolveDuration"
                class="!mb-1"
                type="number"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.PLACEHOLDER')
                "
                @blur="v$.autoResolveDuration.$touch"
              />
              <div class="mb-3 text-xs text-n-slate-11">
                {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.NOTE') }}
              </div>
              <span v-if="v$.autoResolveDuration.$error" class="message">
                {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.ERROR') }}
              </span>
            </label>
            <label
              v-if="showAutoResolutionConfig"
              :class="{ error: v$.autoResolveEmptyDuration.$error }"
            >
              {{
                $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_EMPTY_DURATION.LABEL')
              }}
              <input
                v-model="autoResolveEmptyDuration"
                type="number"
                :placeholder="
                  $t(
                    'GENERAL_SETTINGS.FORM.AUTO_RESOLVE_EMPTY_DURATION.PLACEHOLDER'
                  )
                "
                @blur="v$.autoResolveEmptyDuration.$touch"
              />
              <span v-if="v$.autoResolveEmptyDuration.$error" class="message">
                {{
                  $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_EMPTY_DURATION.ERROR')
                }}
              </span>
            </label>
            <label>
              {{
                $t(
                  'GENERAL_SETTINGS.FORM.CONVERSATION_TIMESTAMP_DISPLAY_MODE.LABEL'
                )
              }}
              <select v-model="conversationTimestampDisplayMode">
                <option
                  v-for="mode in [
                    'both',
                    'last_activity_only',
                    'last_customer_message',
                  ]"
                  :key="mode"
                  :value="mode"
                >
                  {{
                    $t(
                      `GENERAL_SETTINGS.FORM.CONVERSATION_TIMESTAMP_DISPLAY_MODE.MODES.${mode}`
                    )
                  }}
                </option>
              </select>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="extendedLabels"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{ $t('GENERAL_SETTINGS.FORM.EXTENDED_LABELS.LABEL') }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="lastAgentEnabled"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{ $t('GENERAL_SETTINGS.FORM.ENABLE_LAST_AGENT.LABEL') }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="allowToggleSignature"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{ $t('GENERAL_SETTINGS.FORM.ALLOW_TOGGLE_SIGNATURE.LABEL') }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="wallboardChatStatusEnabled"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{
                  $t(
                    'GENERAL_SETTINGS.FORM.WALLBOARD_CHAT_STATUS_ENABLED.LABEL'
                  )
                }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="showFullAttachmentNames"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{
                  $t('GENERAL_SETTINGS.FORM.SHOW_FULL_ATTACHMENT_NAMES.LABEL')
                }}
              </span>
            </label>
            <label>
              {{ $t('GENERAL_SETTINGS.FORM.PUSH_NOTIFICATION_MODE.LABEL') }}
              <select v-model="pushNotificationMode">
                <option
                  v-for="mode in ['all_inboxes', 'chat_only']"
                  :key="mode"
                  :value="mode"
                >
                  {{
                    $t(
                      `GENERAL_SETTINGS.FORM.PUSH_NOTIFICATION_MODE.MODES.${mode}`
                    )
                  }}
                </option>
              </select>
            </label>
            <label>
              {{ $t('GENERAL_SETTINGS.FORM.PASSWORD_LOGIN_MODE.LABEL') }}
              <select v-model="passwordLoginMode">
                <option
                  v-for="mode in [
                    'allowed_for_all',
                    'allowed_for_agents_only',
                    'password_login_disabled',
                  ]"
                  :key="mode"
                  :value="mode"
                >
                  {{
                    $t(
                      `GENERAL_SETTINGS.FORM.PASSWORD_LOGIN_MODE.MODES.${mode}`
                    )
                  }}
                </option>
              </select>
            </label>
          </div>
        </div>
      </form>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>

    <div class="flex-grow flex-shrink min-w-0 mt-3 overflow-auto">
      <form v-if="!uiFlags.isFetchingItem" @submit.prevent="updateAccount">
        <div
          class="flex flex-row border-b border-slate-25 dark:border-slate-800"
        >
          <div
            class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
          >
            <h4 class="text-lg font-medium text-black-900 dark:text-slate-200">
              {{ $t('GENERAL_SETTINGS.FORM.EMAIL.TITLE') }}
            </h4>
            <p>
              {{ $t('GENERAL_SETTINGS.FORM.EMAIL.NOTE') }}
            </p>
          </div>
          <div class="p-4 flex-grow-0 flex-shrink-0 flex-[70%]">
            <label class="flex gap-2 pb-4">
              <input
                v-model="includePreviousEmailMessage"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{
                  $t(
                    'GENERAL_SETTINGS.FORM.INCLUDE_PREVIOUS_EMAIL_MESSAGE.LABEL'
                  )
                }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="resolveNewConversation"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{ $t('GENERAL_SETTINGS.FORM.RESOLVE_NEW_CONVERSATION.LABEL') }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="moveConversationsByOrders"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{
                  $t('GENERAL_SETTINGS.FORM.MOVE_CONVERSATIONS_BY_ORDERS.LABEL')
                }}
              </span>
            </label>
            <label class="flex gap-2 pb-4">
              <input
                v-model="blockDomainsWithLowReputation"
                type="checkbox"
                class="checkbox"
              />
              <span>
                {{
                  $t(
                    'GENERAL_SETTINGS.FORM.BLOCK_DOMAINS_WITH_LOW_REPUTATION.LABEL'
                  )
                }}
              </span>
            </label>
          </div>
        </div>
      </form>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>

    <div class="flex-grow flex-shrink min-w-0 mt-3 overflow-auto">
      <form v-if="!uiFlags.isFetchingItem" @submit.prevent="updateAccount">
        <div
          class="flex flex-row border-b border-slate-25 dark:border-slate-800"
        >
          <div
            class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
          >
            <h4 class="text-lg font-medium text-black-900 dark:text-slate-200">
              {{ $t('GENERAL_SETTINGS.FORM.PHONE.TITLE') }}
            </h4>
            <p>{{ $t('GENERAL_SETTINGS.FORM.PHONE.NOTE') }}</p>
          </div>
          <div class="p-4 flex-grow-0 flex-shrink-0 flex-[70%]">
            <label>
              {{ $t('GENERAL_SETTINGS.FORM.PHONE.SIP_HOST.LABEL') }}
              <input :value="phoneDomain" type="text" readonly disabled />
            </label>
            <template v-if="phoneDomain">
              <label>
                {{ $t('GENERAL_SETTINGS.FORM.PHONE.DIALING_MODE.LABEL') }}
                <select v-model="dialingMode">
                  <option
                    v-for="mode in ['direct', 'indirect']"
                    :key="mode"
                    :value="mode"
                  >
                    {{
                      $t(
                        `GENERAL_SETTINGS.FORM.PHONE.DIALING_MODE.MODES.${mode}`
                      )
                    }}
                  </option>
                </select>
              </label>
              <label>
                {{
                  $t('GENERAL_SETTINGS.FORM.PHONE.CALL_RECORDING_ACCESS.LABEL')
                }}
                <select v-model="callRecordingAccess">
                  <option
                    v-for="mode in [
                      'admin_only',
                      'admin_and_assigned_agent',
                      'inbox_members',
                    ]"
                    :key="mode"
                    :value="mode"
                  >
                    {{
                      $t(
                        `GENERAL_SETTINGS.FORM.PHONE.CALL_RECORDING_ACCESS.MODES.${mode}`
                      )
                    }}
                  </option>
                </select>
              </label>
            </template>
          </div>
        </div>
      </form>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>

    <div class="flex flex-row">
      <div class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0">
        <h4 class="text-lg font-medium text-black-900 dark:text-slate-200">
          {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.TITLE') }}
        </h4>
        <p>
          {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.NOTE') }}
        </p>
      </div>
      <div class="p-4 flex-grow-0 flex-shrink-0 flex-[70%]">
        <woot-code :script="getAccountId" />
      </div>
    </div>
    <div v-if="!uiFlags.isFetchingItem && isOnChatwootCloud">
      <div
        class="flex flex-row pt-4 mt-2 border-t border-slate-25 dark:border-slate-800 text-black-900 dark:text-slate-300"
      >
        <div
          class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
        >
          <h4 class="text-lg font-medium text-black-900 dark:text-slate-200">
            {{ $t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.TITLE') }}
          </h4>
          <p>
            {{ $t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.NOTE') }}
          </p>
        </div>
        <div class="p-4 flex-grow-0 flex-shrink-0 flex-[70%]">
          <div v-if="isMarkedForDeletion">
            <div
              class="p-4 flex-grow-0 flex-shrink-0 flex-[50%] bg-red-50 dark:bg-red-900 rounded"
            >
              <p class="mb-4">
                {{
                  $t(
                    `GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SCHEDULED_DELETION.MESSAGE_${markedForDeletionReason === 'manual_deletion' ? 'MANUAL' : 'INACTIVITY'}`,
                    {
                      deletionDate: formattedDeletionDate,
                    }
                  )
                }}
              </p>
              <NextButton
                :label="
                  $t(
                    'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SCHEDULED_DELETION.CLEAR_BUTTON'
                  )
                "
                color="ruby"
                :is-loading="uiFlags.isUpdating"
                @click="clearDeletionMark"
              />
            </div>
          </div>
          <div v-if="!isMarkedForDeletion">
            <NextButton
              :label="$t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.BUTTON_TEXT')"
              color="ruby"
              @click="openDeletePopup()"
            />
          </div>
        </div>
      </div>
      <WootConfirmDeleteModal
        v-if="showDeletePopup"
        v-model:show="showDeletePopup"
        :title="$t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.TITLE')"
        :message="$t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.MESSAGE')"
        :confirm-text="
          $t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.BUTTON_TEXT')
        "
        :reject-text="
          $t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.DISMISS')
        "
        :confirm-value="name"
        :confirm-place-holder-text="confirmPlaceHolderText"
        @on-confirm="markAccountForDeletion"
        @on-close="closeDeletePopup"
      />
    </div>
    <div class="p-4 text-sm text-center">
      {{ `Build ${globalConfig.gitSha?.slice(0, 7)}` }}
    </div>
  </div>
</template>
